import React from "react";
import { Link } from "react-router-dom";
import { Sprout, Tractor, Leaf, Sun, Factory, Package, Home as HomeIcon, Globe, Droplets, Recycle, Users, Lightbulb, Check, ArrowRight, HeartHandshake, GraduationCap, TreePine } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionHead } from "@/components/SectionHead";
import { Reveal, Stagger, StaggerItem, ParallaxImage, Marquee } from "@/lib/motion";
import { IMG } from "@/data/images";

const PRINCIPLES = [
  { icon: Sprout, title: "Responsible Farming", text: "Working alongside farmers to encourage responsible cultivation, biodiversity, and efficient use of natural resources." },
  { icon: Droplets, title: "Water Stewardship", text: "Every drop matters. We promote thoughtful irrigation practices and initiatives that reduce unnecessary water use." },
  { icon: Globe, title: "Protecting Biodiversity", text: "Healthy farms are living ecosystems. We support practices that help preserve native plants, pollinators, and wildlife." },
  { icon: Recycle, title: "Thoughtful Packaging", text: "We continually explore packaging solutions that better balance product protection with environmental responsibility." },
  { icon: Users, title: "Empowering Communities", text: "Strong farms build strong communities. We invest in long-term partnerships with farmers to create lasting value for everyone." },
  { icon: Lightbulb, title: "Innovation for Tomorrow", text: "We invest in better farming practices, better processing, and responsible innovation for a better tomorrow." },
];

const JOURNEY8 = [
  { icon: Sprout, title: "Seed", text: "Carefully selected for quality & resilience" },
  { icon: Tractor, title: "Farm", text: "Partnering with responsible farmers" },
  { icon: Leaf, title: "Responsible Cultivation", text: "Nurturing soil, crops & biodiversity" },
  { icon: Sun, title: "Natural Harvest", text: "Harvested at the right time" },
  { icon: Factory, title: "Efficient Processing", text: "Hygienic processing with minimum impact" },
  { icon: Package, title: "Responsible Packaging", text: "Thoughtful packaging for a greener future" },
  { icon: HomeIcon, title: "Your Family", text: "Pure nutrition, delivered with care" },
  { icon: Globe, title: "Better Tomorrow", text: "For our planet & future generations" },
];

const TEAM = [
  { label: "Farmers", sub: "The heart of our journey", image: IMG.heroFarm },
  { label: "Agronomists", sub: "Guiding practices for healthy soil", image: IMG.heroExperience },
  { label: "Food Scientists", sub: "Ensuring safety & nutrition", image: IMG.heroQuality },
  { label: "Processing Teams", sub: "Maintaining hygiene & efficiency", image: IMG.heroCollection },
  { label: "Logistics Partners", sub: "Delivering responsibly", image: IMG.heroStory },
  { label: "Our Customers", sub: "Your trust inspires us", image: IMG.familyDinner },
];

const MEANINGS = [
  { icon: Leaf, title: "For Nature", points: ["Protect ecosystems", "Respect resources", "Grow responsibly"], image: IMG.heroSustain },
  { icon: Users, title: "For People", points: ["Support farming communities", "Promote safe workplaces", "Build long-term relationships"], image: IMG.heroExperience },
  { icon: Globe, title: "For Future Generations", points: ["Think beyond today's harvest", "Make decisions that create lasting value"], image: IMG.soilHands },
];

const IMPACT = [
  { icon: TreePine, label: "Trees Supported" },
  { icon: Droplets, label: "Water Conservation Initiatives" },
  { icon: Tractor, label: "Farmers Working With Us" },
  { icon: Recycle, label: "Sustainable Packaging Progress" },
  { icon: Leaf, label: "Acres Under Responsible Cultivation" },
  { icon: GraduationCap, label: "Farmer Training Sessions" },
];

export default function Sustainability() {
  return (
    <div data-testid="sustainability-page">
      <PageHero
        tall
        eyebrow="Sustainability"
        lines={["Growing Goodness.", "Protecting Tomorrow."]}
        sub={"\u201CWe don\u2019t inherit the Earth from our ancestors. We borrow it from our children.\u201D"}
        image={IMG.heroSustain}
      />

      <section className="py-20 sm:py-28">
        <div className="container-nb grid lg:grid-cols-[1fr_1fr_1.4fr] gap-10">
          <Reveal>
            <p className="eyebrow mb-4">Our Belief</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-forest leading-[1.2]">Healthy Soil.<br />Healthy Plants.<br />Healthy Families.<br />Healthy Planet.</h2>
            <p className="mt-6 text-sm text-mutedgreen leading-relaxed">Every Nutribest product begins with nature. That is why protecting nature is not a choice — it is our responsibility.</p>
            <p className="mt-4 font-serif italic text-forest">Our goal is simple: leave every farm healthier than we found it.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <ParallaxImage src={IMG.soilHands} alt="Hands holding fertile soil" className="rounded-2xl h-72 lg:h-full shadow-xl shadow-forest/10" speed={10} />
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 gap-4">
            {PRINCIPLES.map(({ icon: Icon, title, text }) => (
              <StaggerItem key={title}>
                <div data-testid={`principle-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="h-full bg-white border border-forest/10 rounded-2xl p-5 hover:border-gold/40 hover:shadow-lg hover:shadow-forest/5 transition-all duration-500">
                  <Icon size={20} strokeWidth={1.5} className="text-gold" />
                  <h3 className="mt-3 text-xs font-bold uppercase tracking-[0.12em] text-forest">{title}</h3>
                  <p className="mt-2 text-[11px] text-mutedgreen leading-relaxed">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-creamdark/40 border-y border-forest/10">
        <div className="container-nb">
          <SectionHead eyebrow="Our journey towards sustainability" title="Every step touches the future." />
          <Stagger className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-5">
            {JOURNEY8.map(({ icon: Icon, title, text }, i) => (
              <StaggerItem key={title}>
                <div data-testid={`sustain-step-${i + 1}`} className="text-center">
                  <span className="mx-auto w-14 h-14 rounded-full bg-white border border-forest/15 flex items-center justify-center text-forest hover:border-gold hover:text-gold transition-colors duration-500"><Icon size={19} strokeWidth={1.5} /></span>
                  <h4 className="mt-3 text-[10px] font-bold uppercase tracking-[0.12em] text-forest leading-tight">{i + 1}. {title}</h4>
                  <p className="mt-1.5 text-[10px] text-mutedgreen leading-relaxed">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container-nb">
          <SectionHead eyebrow="Sustainability is a team effort" title="Every hand that touches a Nutribest product contributes to it." />
          <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {TEAM.map(({ label, sub, image }) => (
              <StaggerItem key={label}>
                <div className="group rounded-2xl overflow-hidden relative h-52">
                  <img src={image} alt={label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forestdeep/85 via-forestdeep/10 to-transparent" />
                  <div className="absolute bottom-0 p-4">
                    <p className="font-serif text-cream text-lg">{label}</p>
                    <p className="text-cream/70 text-[10px]">{sub}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="container-nb">
          <SectionHead eyebrow="What sustainability means at Nutribest" title="Three commitments. One future." />
          <Stagger className="grid md:grid-cols-3 gap-6">
            {MEANINGS.map(({ icon: Icon, title, points, image }) => (
              <StaggerItem key={title}>
                <div data-testid={`meaning-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-2xl overflow-hidden relative h-80 group">
                  <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forestdeep/90 via-forestdeep/30 to-forestdeep/10" />
                  <div className="relative h-full flex flex-col justify-end p-7">
                    <span className="w-11 h-11 rounded-full bg-cream/15 backdrop-blur border border-cream/25 flex items-center justify-center text-goldlight mb-4"><Icon size={19} /></span>
                    <h3 className="font-serif text-2xl text-cream">{title}</h3>
                    <ul className="mt-3 space-y-1.5">
                      {points.map((p) => <li key={p} className="text-cream/80 text-xs flex items-center gap-2"><Check size={11} className="text-goldlight" /> {p}</li>)}
                    </ul>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-16 bg-creamdark/40 border-y border-forest/10">
        <div className="container-nb">
          <SectionHead eyebrow="Small actions. Big impact." title="Committed to continuous improvement and transparent progress." className="mb-10" />
          <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {IMPACT.map(({ icon: Icon, label }) => (
              <StaggerItem key={label}>
                <div className="text-center">
                  <span className="mx-auto w-12 h-12 rounded-full border border-gold/40 bg-white flex items-center justify-center text-forest"><Icon size={19} strokeWidth={1.5} /></span>
                  <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.12em] text-forest leading-tight">{label}</p>
                  <p className="mt-1 text-[10px] font-serif italic text-gold">Growing Every Year</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-forestdeep grain relative">
        <div className="container-nb grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl text-cream uppercase tracking-wide">Our promise extends<br />beyond our products.</h2>
            <p className="mt-4 text-cream/60 text-xs uppercase tracking-[0.2em]">Every decision we make considers:</p>
            <ul className="mt-6 space-y-3">
              {["Your family's health", "Our farmers' livelihoods", "The health of our soil", "The future of our planet"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-cream/85 text-sm"><span className="w-5 h-5 rounded-full bg-gold flex items-center justify-center"><Check size={11} strokeWidth={3} className="text-white" /></span> {t}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15} className="lg:text-right">
            <p className="font-serif text-3xl sm:text-4xl text-cream leading-snug">Because every family deserves food they never have to question.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container-nb grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <ParallaxImage src={IMG.heroStory} alt="Sunrise over green farms" className="rounded-3xl h-72 lg:h-96" speed={10} />
            <p className="mt-6 font-serif text-2xl text-forest">This is just the beginning.</p>
            <p className="mt-3 text-sm text-mutedgreen leading-relaxed max-w-md">We know sustainability is a journey — not a destination. As Nutribest grows, we will continue learning, improving, and investing in better ways to care for our people, our farms, and our planet.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-white border border-forest/10 rounded-3xl p-8 sm:p-12">
              <p className="eyebrow mb-4">Grow With Us</p>
              <p className="text-sm text-mutedgreen leading-relaxed">Whether you're a customer, farmer, researcher, or partner, every thoughtful choice helps build a healthier future.</p>
              <div className="mt-8 space-y-3">
                <Link to="/collection" data-testid="sustainability-explore-button" className="group flex items-center justify-between bg-forest text-cream text-xs font-semibold uppercase tracking-[0.16em] px-7 py-4 rounded-full hover:bg-gold transition-colors duration-300">
                  Explore All Products <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/farm-to-family" data-testid="sustainability-partner-button" className="group flex items-center justify-between border border-forest/25 text-forest text-xs font-semibold uppercase tracking-[0.16em] px-7 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300">
                  Partner With Nutribest <HeartHandshake size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee items={["Leave every farm healthier than we found it.", "Responsible today. Healthier tomorrow.", "Small actions. Big impact.", "Trust grows naturally."]} />
    </div>
  );
}
